---
title: "Mean or median: which should you use when data has outliers?"
description: "Compare mean and median, see how extreme values change the result, and choose the right measure for prices, salaries, durations and workloads."
locale: "en"
date: "2026-07-23"
tags: ["mean", "median", "outliers"]
relatedTools: ["average-median-calculator"]
translations:
  ru: "srednee-ili-mediana-chto-vybrat"
faq:
  - q: "Which better represents a typical value: mean or median?"
    a: "With clear outliers or a long-tailed distribution, the median is usually closer to a typical observation. The mean is more useful when the total contribution of every value matters."
  - q: "How do I find the median of an even number of values?"
    a: "Sort the values and take the arithmetic mean of the two central numbers. For 2, 4, 7, 9, the median is 5.5."
  - q: "Does a large gap between mean and median prove there is an outlier?"
    a: "No. The gap is a prompt to inspect the distribution, minimum, maximum and source values, but it is not an outlier test by itself."
---

Use the **median** first when you need a typical observation and a few values
are much larger or smaller than the rest. Use the **mean** when you need a total
amount distributed across all observations.

| Question | Start with |
| --- | --- |
| What is a typical salary, house price or response time? | Median |
| What is the total workload per person or average resource use? | Mean |
| Are measurements fairly uniform with no clear extremes? | Mean and median together |
| Is a small dataset dominated by one suspicious value? | Source values, minimum and maximum, then both measures |

Paste the values into the [mean and median calculator](/srednee-mediana). It
shows both measures alongside count, sum, minimum, maximum and range. That
context is more informative than either result on its own.

## How one extreme value changes the answer

Consider five task durations:

`48, 52, 55, 57, 310`

The mean is `104.4`: the sum of `522` divided by five. The median is `55`, the
middle value after sorting. If these are minutes spent on routine tasks, the
median describes an ordinary case more closely, while `310` looks like a
separate long incident.

The mean is not wrong, though. The five tasks took `522` minutes in total, or
`104.4` minutes per task on average. Budgeting the median of `55` minutes for
each task would reserve only `275` minutes and underbudget the batch by `247`
minutes. Capacity planning must include the long incident when it represents
real work.

The useful distinction is:

- the median marks the centre of the ordered observations;
- the mean includes every value's contribution to the total;
- the right choice follows from the decision you need to make.

## When the median is more useful

The median is resistant to a small number of extreme values. It is often useful
for prices, income, waiting times, request durations and similar data where
most observations cluster together while a long tail extends in one direction.

A median price of 600,000 means at least half the observations are no higher
and at least half are no lower. It does not mean most items cost exactly
600,000, and it does not estimate the total value of every item.

With an even number of observations, sort the list and average the two central
values. In `2, 4, 7, 9`, those values are `4` and `7`, so the median is `5.5`.

## When the mean cannot be replaced by the median

Use the mean when the sum has operational meaning:

- requests a team must process;
- material consumed by a production batch;
- total equipment runtime;
- a score defined by an established averaging rule.

An extreme value may be rare but valid. Do not delete it merely to make the
summary look stable. First check the source: were units mixed, was an extra
zero added, and does the observation belong to the same time period and group?

## Can the mean-median gap identify an outlier?

A wide gap is a useful reason to inspect the data, not a complete outlier test.
A skewed distribution can be natural: a duration cannot go below zero, but it
can occasionally become very large. Mean and median can also be close while
unusual high and low values balance each other.

Inspect the minimum, maximum, range and original list. Formal outlier detection
requires an explicit method and subject context. The calculator reports
descriptive statistics; it does not label individual observations as errors.

## A quick workflow for pasted data

1. Copy one numeric column from a spreadsheet or document.
2. Paste it into the [calculator](/srednee-mediana). It accepts spaces, line
   breaks, tabs, semicolons and unambiguous decimal points or commas.
3. Check the accepted count and any ignored tokens. A column heading or stray
   note should not disappear into the calculation unnoticed.
4. Compare mean and median, then review minimum, maximum and range.
5. Name the measure in your conclusion: write “median response time,” not just
   “the average.”

Keep the source list and your reason for selecting the measure. That makes the
result reproducible and prevents choosing whichever number looks better after
the calculation.
